import { apiClient } from './apiClient';
import rpcSettings from './rpc/settings';

/**
 * Helper to build RPC settings base URL
 */
const settingsRpcBase = (company: string): string => `/api/${company}/rpc/settings`;

/**
 * Get manager profile
 */
export async function getManagerMe(company: string): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-manager-me`, {}, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get manager profile');
  }
  return data.data;
}

/**
 * Get auth/me (for 2FA status and login alert)
 * CRITICAL: This endpoint is optimized to use req.user from authMiddleware (no DB query)
 * Expected latency: < 50ms (should rarely exceed 100ms)
 * 
 * Backend optimization:
 * - Uses req.user from authMiddleware (no DB query)
 * - Fallback to cache/DB only if req.user unavailable (should never happen)
 * - Timeout: 5s (backend has 4s internal timeout, frontend adds 1s buffer)
 */
export async function getAuthMe(company: string): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-auth-me`, {}, {
    timeout: 5000, // 5s timeout - backend uses req.user (< 50ms) or cache/DB fallback (< 2s)
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get auth info');
  }
  return data.data;
}

/**
 * Get admin profile
 * CRITICAL: This endpoint is optimized to use req.user from authMiddleware (no DB query)
 * Expected latency: < 50ms (should rarely exceed 100ms)
 * 
 * Backend optimization:
 * - Uses req.user from authMiddleware (no DB query)
 * - Fallback to cache/DB only if req.user unavailable (should never happen)
 * - Timeout: 15s (increased to handle slow DB queries and network issues)
 */
export async function getAdminMe(company: string): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-admin-me`, {}, {
    timeout: 15000, // 15s timeout - increased to handle slow DB queries and network latency
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get admin profile');
  }
  return data.data;
}

/**
 * Update current user settings (language) - works for all roles including superadmin.
 * Uses company-scoped POST so companyDbMiddleware + auth set req.models (avoids 500 on PATCH /api/users/me/settings).
 */
export async function updateUserSettings(
  company: string,
  payload: { language: 'he' | 'en' | 'ar' }
): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/update-user-settings`, payload, {
    timeout: 5000,
  });

  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to update settings');
  }
  return data.data;
}

/**
 * Update admin profile
 */
export async function updateAdminMe(company: string, payload: { email?: string; phone?: string }): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/update-admin-me`, payload, {
    timeout: 12000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to update admin profile');
  }
  return data.data;
}

/**
 * Get Google status (supports both managerId and adminId)
 */
export async function getGoogleStatus(company: string, managerId?: string, adminId?: string): Promise<any> {
  const payload: any = {};
  if (managerId) payload.managerId = managerId;
  if (adminId) payload.adminId = adminId;
  
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-google-status`, payload, {
    timeout: 10000,
  });
  
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get Google status');
  }
  
  return data.data;
}

/**
 * Get manager stats
 */
export async function getManagerStats(company: string): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-manager-stats`, {}, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get manager stats');
  }
  return data.data;
}

/**
 * Get manager preferences
 */
export async function getManagerPreferences(company: string): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-manager-preferences`, {}, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get manager preferences');
  }
  return data.data;
}

/**
 * Update manager preferences
 */
export async function updateManagerPreferences(company: string, preferences: any): Promise<void> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/update-manager-preferences`, { preferences }, {
    timeout: 12000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to update manager preferences');
  }
}

/**
 * Get my sessions
 */
export async function getMySessions(company: string): Promise<{ sessions: any[] }> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-my-sessions`, {});
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get sessions');
  }
  return data.data || { sessions: [] };
}

/**
 * Logout a specific session
 */
export async function logoutSession(company: string, sessionId: string): Promise<void> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/logout-session`, { sessionId });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to logout session');
  }
}

/**
 * Logout all sessions
 */
export async function logoutAllSessions(company: string): Promise<void> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/logout-all-sessions`, {});
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to logout all sessions');
  }
}

/**
 * Rename a session device
 */
export async function renameSessionDevice(company: string, sessionId: string, deviceName: string): Promise<{ deviceName: string }> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/rename-session-device`, { sessionId, deviceName });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to rename session device');
  }
  return data.data;
}

/**
 * Disconnect Google (supports both managerId and adminId)
 */
/**
 * Toggle WhatsApp messaging (company-level setting)
 * EXACTLY like toggleLoginAlert but for company settings
 */
export async function toggleWhatsAppMessaging(company: string, enabled: boolean) {
  try {
    const response = await rpcSettings.toggleWhatsAppMessaging(enabled);
    const data = response?.data || response;
    // RPC returns { ok: true, data } or { ok: false, error }
    if (data?.ok) {
      return data.data || data;
    }
    throw new Error(data?.error || 'Failed to toggle WhatsApp messaging');
  } catch (error: any) {
    console.error('[settingsApi] toggleWhatsAppMessaging error:', error);
    throw error;
  }
}

/**
 * Toggle SMS messaging (company-level setting)
 * EXACTLY like toggleWhatsAppMessaging but for SMS
 */
export async function toggleSmsMessaging(company: string, enabled: boolean) {
  try {
    const response = await rpcSettings.toggleSmsMessaging(enabled);
    const data = response?.data || response;
    // RPC returns { ok: true, data } or { ok: false, error }
    if (data?.ok) {
      return data.data || data;
    }
    throw new Error(data?.error || 'Failed to toggle SMS messaging');
  } catch (error: any) {
    console.error('[settingsApi] toggleSmsMessaging error:', error);
    throw error;
  }
}

export async function disconnectGoogle(company: string, managerId?: string, adminId?: string): Promise<void> {
  const payload: any = {};
  if (managerId) payload.managerId = managerId;
  if (adminId) payload.adminId = adminId;
  
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/disconnect-google`, payload, {
    timeout: 12000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to disconnect Google');
  }
}

/**
 * Toggle login alert notifications
 */
export async function toggleLoginAlert(company: string, enabled: boolean): Promise<{ message?: string }> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/toggle-login-alert`, { enabled }, {
    timeout: 12000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to toggle login alert');
  }
  return data.data || {};
}

/**
 * Get admin sessions (for admin users with view_sessions permission)
 */
export async function getAdminSessions(company: string, params?: { userId?: string; page?: number; limit?: number }): Promise<{ sessions: any[]; pagination: any }> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-admin-sessions`, params || {}, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get admin sessions');
  }
  return data.data || { sessions: [], pagination: {} };
}

/**
 * Get admin devices
 * Returns: { devices, device_lock_enabled, device_approval_mode, bypass_roles }
 */
export async function getAdminDevices(company: string, page: number = 1, limit: number = 20): Promise<{
  devices: any[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  stats?: {
    total: number;
    pending: number;
    approved: number;
  };
  device_lock_enabled?: boolean;
  device_approval_mode?: 'once' | 'every_login';
  bypass_roles?: string[];
}> {
  const url = `${settingsRpcBase(company)}/get-admin-devices`;
/*   console.log('[settingsApi.getAdminDevices] START - Making API call:', {
    company,
    page,
    limit,
    url,
    fullUrl: `/api/${company}/rpc/settings/get-admin-devices`,
    timestamp: new Date().toISOString()
  }); */
  
  if (!company || company.trim() === '') {
    console.error('[settingsApi.getAdminDevices] ERROR - Empty company parameter');
    throw new Error('Company parameter is required');
  }
  
  try {
/*     console.log('[settingsApi.getAdminDevices] Sending POST request to:', url, 'with page:', page, 'limit:', limit);
 */    const startTime = Date.now();
    const { data } = await apiClient.post(url, { page, limit }, {
      timeout: 10000,
    });
    const duration = Date.now() - startTime;
    
/*     console.log('[settingsApi.getAdminDevices] API Response received:', {
      company,
      duration: `${duration}ms`,
      status: 'success',
      hasData: !!data,
      dataOk: data?.ok,
      hasDataData: !!data?.data,
      responseKeys: data ? Object.keys(data) : [],
      fullResponse: data
    }); */
    
    if (!data?.ok) {
      console.error('[settingsApi.getAdminDevices] ERROR - Response not OK:', {
        company,
        ok: data?.ok,
        error: data?.error,
        fullResponse: data
      });
      throw new Error(data?.error || 'Failed to get admin devices');
    }
    
    // Ensure we always return the full structure with device_lock_enabled
    const responseData = data.data || {};
/*     console.log('[settingsApi.getAdminDevices] Processing response data:', {
      company,
      hasDevices: !!responseData.devices,
      devicesIsArray: Array.isArray(responseData.devices),
      devicesCount: responseData.devices?.length || 0,
      device_lock_enabled: responseData.device_lock_enabled,
      device_approval_mode: responseData.device_approval_mode,
      bypass_roles: responseData.bypass_roles,
      responseDataKeys: Object.keys(responseData)
    }); */
    
    const result = {
      devices: responseData.devices || [],
      device_lock_enabled: responseData.device_lock_enabled !== undefined ? responseData.device_lock_enabled : false,
      device_approval_mode: responseData.device_approval_mode || 'once',
      bypass_roles: responseData.bypass_roles || []
    };
    
/*     console.log('[settingsApi.getAdminDevices] SUCCESS - Returning result:', {
      company,
      devicesCount: result.devices.length,
      device_lock_enabled: result.device_lock_enabled,
      device_approval_mode: result.device_approval_mode,
      bypass_rolesCount: result.bypass_roles.length
    }); */
    
    return result;
  } catch (error: any) {
    console.error('[settingsApi.getAdminDevices] ERROR - API call failed:', {
      company,
      url,
      error: error.message,
      stack: error.stack,
      response: error.response?.data,
      status: error.response?.status,
      statusText: error.response?.statusText,
      config: error.config ? {
        url: error.config.url,
        method: error.config.method,
        baseURL: error.config.baseURL
      } : null
    });
    throw error;
  }
}

/**
 * Approve device
 */
export async function approveDevice(company: string, deviceId: string): Promise<void> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/approve-device`, { deviceId }, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to approve device');
  }
}

/**
 * Block device
 */
export async function blockDevice(company: string, deviceId: string): Promise<void> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/block-device`, { deviceId }, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to block device');
  }
}

/**
 * Delete device
 */
export async function deleteDevice(company: string, deviceId: string): Promise<void> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/delete-device`, { deviceId }, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to delete device');
  }
}

/**
 * Get sender configuration
 */
export async function getSender(company: string): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-sender`, {}, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get sender');
  }
  return data.data;
}

/**
 * Create or update sender
 */
export async function createSender(company: string, payload: { fromNumber: string; displayName?: string; twilioPhoneSid?: string }): Promise<any> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/create-sender`, payload, {
    timeout: 12000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to create sender');
  }
  return data.data;
}

/**
 * Delete (deactivate) sender
 */
export async function deleteSender(company: string): Promise<void> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/delete-sender`, {}, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to delete sender');
  }
}

/**
 * Get bulk templates (SMS or Email)
 */
export async function getBulkTemplates(company: string, kind: 'sms' | 'email' = 'sms'): Promise<{ items: any[] }> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-bulk-templates`, { kind }, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get bulk templates');
  }
  return data.data || { items: [] };
}

/**
 * Get company features
 */
export async function getCompanyFeatures(company: string): Promise<{ enabledFeatures: string[]; hiddenFromSidebar: string[]; companyName?: string }> {
  const { data } = await apiClient.post(`${settingsRpcBase(company)}/get-company-features`, {}, {
    timeout: 10000,
  });
  if (!data?.ok) {
    throw new Error(data?.error || 'Failed to get company features');
  }
  return data.data || { enabledFeatures: [], hiddenFromSidebar: [] };
}
